import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  User as FirebaseUser,
} from 'firebase/auth';
import { auth, ACTIVE_DB_KEY, DEFAULT_DB } from '../services/firebase';

interface AuthContextData {
  user: FirebaseUser | null;
  loading: boolean;
  isAdmin: boolean;
  activeDatabaseId: string;
  setActiveDatabase: (databaseId: string) => void;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [activeDatabaseId, setActiveDatabaseIdState] = useState<string>(
    () => localStorage.getItem(ACTIVE_DB_KEY) || DEFAULT_DB
  );

  const setActiveDatabase = (databaseId: string) => {
    localStorage.setItem(ACTIVE_DB_KEY, databaseId);
    setActiveDatabaseIdState(databaseId);
  };

  useEffect(() => {
    // Listen to Firebase auth state changes
    const unsubscribe = onAuthStateChanged(auth, async firebaseUser => {
      if (firebaseUser) {
        setUser(firebaseUser);

        // Check admin claims
        const idTokenResult = await firebaseUser.getIdTokenResult();
        setIsAdmin(!!idTokenResult.claims.admin);
      } else {
        setUser(null);
        setIsAdmin(false);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      await signInWithEmailAndPassword(auth, email, password);
      // User state will be updated by onAuthStateChanged listener
    } catch (error: unknown) {
      console.error('Sign in error:', error);
      if (error instanceof Error) {
        throw new Error(error.message || 'Failed to sign in');
      }
      throw new Error('Failed to sign in');
    }
  };

  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
      // User state will be updated by onAuthStateChanged listener
    } catch (error: unknown) {
      console.error('Sign out error:', error);
      if (error instanceof Error) {
        throw new Error(error.message || 'Failed to sign out');
      }
      throw new Error('Failed to sign out');
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isAdmin,
        activeDatabaseId,
        setActiveDatabase,
        signIn,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
